import {
  Activity,
  CalendarDays,
  CheckCircle,
  DollarSign,
  FileText,
  Plus,
  RefreshCw,
  UserPlus,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  parseBudgetToNumber,
  useDashboardAnalytics,
} from "@/hooks/dashboard/useDashboardAnalytics";
import { useDashboardData } from "@/hooks/dashboard/useDashboardData";
import { useDashboardRefresh } from "@/hooks/dashboard/useDashboardRefresh";

const DashboardPage = () => {
  const navigate = useNavigate();

  // Data fetching
  const {
    projectsData,
    recentProjectsData,
    customersData,
    allCustomersData,
    recentCustomersData,
    isLoading: dataLoading,
  } = useDashboardData();

  // Analytics calculations
  const { projectStats, customerAnalytics } = useDashboardAnalytics({
    projectsData,
    allCustomersData,
  });

  // Refresh and filtering
  const {
    isRefreshing,
    autoRefresh,
    lastRefresh,
    customerFilter,
    filteredRecentCustomers,
    setAutoRefresh,
    setCustomerFilter,
    handleManualRefresh,
  } = useDashboardRefresh({ recentCustomersData, projectsData });

  const projectsLoading = dataLoading;
  const allCustomersLoading = dataLoading;
  const recentCustomersLoading = dataLoading;

  const stats = [
    {
      title: "Total Projects",
      value: projectsLoading ? "..." : projectStats.total.toString(),
      change: `${projectStats.active} active, ${projectStats.draft} draft`,
      trend: "up",
      icon: FileText,
      color: "bg-primary",
    },
    {
      title: "Pipeline Value",
      value: projectsLoading ? "..." : `$${(projectStats.totalPipelineValue / 1000).toFixed(0)}k`,
      change: `Avg: $${(projectStats.averageProjectSize / 1000).toFixed(0)}k per project`,
      trend: "up",
      icon: DollarSign,
      color: "bg-green-600",
    },
    {
      title: "Total Customers",
      value: allCustomersLoading ? "..." : customerAnalytics.totalCustomers.toString(),
      change: `${customerAnalytics.newCustomersThisMonth} new this month`,
      trend: customerAnalytics.customerGrowthRate >= 0 ? "up" : "down",
      icon: Users,
      color: "bg-blue-600",
    },
    {
      title: "Completed Value",
      value: projectsLoading
        ? "..."
        : `$${(projectStats.completedProjectsValue / 1000).toFixed(0)}k`,
      change:
        projectStats.total > 0
          ? `${((projectStats.completed / projectStats.total) * 100).toFixed(0)}% completion rate`
          : "No projects yet",
      trend: "up",
      icon: CheckCircle,
      color: "bg-green-500",
    },
  ];

  // Transform real project data for display
  const recentProjects = recentProjectsData
    ? recentProjectsData.map((project) => ({
        id: project.id,
        customer:
          project.customer_name ||
          `${project.first_name || ""} ${project.last_name || ""}`.trim() ||
          "Unknown Customer",
        projectName: project.project_name || `Project #${project.id}`,
        location: project.city
          ? `${project.city}${project.address ? ", " + project.address : ""}`
          : "Location not specified",
        status: project.status,
        date: new Date(project.created_at).toLocaleDateString(),
        budget: project.budget || "Budget not set",
      }))
    : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "active":
      case "sent":
        return "bg-blue-100 text-blue-800";
      case "pending":
      case "under-review":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
      case "accepted":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "draft":
        return "Draft";
      case "active":
        return "Active";
      case "pending":
        return "Pending";
      case "sent":
        return "Quote Sent";
      case "under-review":
        return "Under Review";
      case "completed":
        return "Completed";
      case "accepted":
        return "Accepted";
      case "rejected":
        return "Rejected";
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onNewProject={() => navigate("/new-project")}
        onViewProjects={() => navigate("/projects")}
      />

      <main className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col space-y-4 lg:flex-row lg:justify-between lg:items-center lg:space-y-0">
            <div className="flex-1">
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Dashboard</h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                <p className="text-sm lg:text-base text-muted-foreground">
                  Welcome back! Here's an overview of your business.
                </p>
                {autoRefresh && (
                  <Badge variant="secondary" className="text-xs w-fit">
                    Auto-refresh ON
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Refresh Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`flex-1 sm:flex-none ${autoRefresh ? "bg-green-50 border-green-200" : ""}`}
                >
                  <Activity className={`h-4 w-4 mr-1 ${autoRefresh ? "text-green-600" : ""}`} />
                  <span className="hidden sm:inline">Auto</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className="flex-1 sm:flex-none"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? "animate-spin" : ""}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
              </div>
              <Button
                onClick={() => navigate("/new-project")}
                size="lg"
                className="w-full sm:w-auto"
              >
                <Plus className="mr-2 h-5 w-5" />
                New Project
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              const isClickable =
                stat.title === "Total Projects" || stat.title === "Total Customers";
              const handleClick = () => {
                if (stat.title === "Total Projects") {
                  navigate("/projects");
                } else if (stat.title === "Total Customers") {
                  navigate("/customers");
                }
              };

              return (
                <Card
                  key={stat.title}
                  className={isClickable ? "cursor-pointer transition-all hover:scale-105" : ""}
                  onClick={isClickable ? handleClick : undefined}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between space-x-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                        <p className="text-2xl font-bold">{stat.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{stat.change}</p>
                      </div>
                      <div className={`p-3 rounded-full ${stat.color} bg-opacity-10`}>
                        <Icon className={`h-6 w-6 ${stat.color.replace("bg-", "text-")}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Meetings Calendar Quick Action */}
            <Card
              className="cursor-pointer transition-all hover:scale-105 bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200"
              onClick={() => navigate("/meetings/calendar")}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">Meetings</p>
                    <p className="text-2xl font-bold">Calendar</p>
                    <p className="text-xs text-muted-foreground mt-1">View & schedule</p>
                  </div>
                  <div className="p-3 rounded-full bg-purple-600 bg-opacity-10">
                    <CalendarDays className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Projects */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Projects</CardTitle>
            </CardHeader>
            <CardContent>
              {projectsLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                      </div>
                      <div className="text-right">
                        <div className="h-6 bg-gray-200 rounded animate-pulse w-20 mb-1" />
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentProjects.length > 0 ? (
                <div className="space-y-4">
                  {recentProjects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors gap-2"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{project.customer}</h4>
                          <Badge className={`text-xs ${getStatusColor(project.status)}`}>
                            {getStatusLabel(project.status)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{project.projectName}</p>
                        <p className="text-xs text-muted-foreground">{project.location}</p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-sm font-medium">{project.budget}</p>
                        <p className="text-xs text-muted-foreground">{project.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p>No recent projects found.</p>
                  <p className="text-sm mt-1">Create your first project to get started!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* New Customers */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                New Customers
              </CardTitle>
              <Select
                value={customerFilter}
                onValueChange={(value: any) => setCustomerFilter(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  <SelectItem value="has-project">Has Project</SelectItem>
                  <SelectItem value="no-project">No Project Yet</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {recentCustomersLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                      </div>
                      <div className="text-right">
                        <div className="h-6 bg-gray-200 rounded animate-pulse w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredRecentCustomers.length > 0 ? (
                <div className="space-y-4">
                  {filteredRecentCustomers.map((customer: any) => (
                    <div
                      key={customer.id}
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors gap-2"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold">
                          {customer.first_name} {customer.last_name}
                        </h4>
                        <p className="text-sm text-muted-foreground">{customer.email}</p>
                        <p className="text-xs text-muted-foreground">{customer.phone}</p>
                      </div>
                      <div className="text-left sm:text-right">
                        <Badge variant={customer.hasProject ? "default" : "secondary"}>
                          {customer.hasProject
                            ? `${customer.projectCount} ${customer.projectCount === 1 ? "project" : "projects"}`
                            : "No projects"}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(customer.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <UserPlus className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p>No customers match the filter.</p>
                  <p className="text-sm mt-1">Try changing your filter selection.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
